import React, { useRef, useState } from "react";
import { Camera, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { authApi, formatError } from "../lib/api";

/**
 * Avatar — circular display + (optional) edit affordance.
 *
 * Render modes:
 *   • Read-only: just shows the image or initials.
 *   • Editable (`onChanged` provided + `editable` true): clicking the
 *     avatar opens a file picker; the chosen image is resized to 256×256
 *     via canvas, encoded JPEG q=0.85, and PUT to /api/auth/me as a data
 *     URL. Once saved, calls `onChanged(updatedUser)` so the parent can
 *     update its auth state.
 *
 * Initials fallback: first letter of the user's name (or "?").
 */
export const Avatar = ({
  user,
  size = 48,
  editable = false,
  onChanged,
  testId = "avatar",
}) => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const initials = (user?.name || "?").trim().charAt(0).toUpperCase();
  const hasPic = Boolean(user?.avatar_url);

  // Resize the picked file via canvas. Keeps things <140KB so the backend
  // accepts it. We pick "cover" rather than "contain" so portraits don't
  // end up with letterboxing in the circular crop.
  const resizeToDataUrl = (file) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        const target = 256;
        const canvas = document.createElement("canvas");
        canvas.width = target;
        canvas.height = target;
        const ctx = canvas.getContext("2d");
        // Cover-fit
        const ratio = Math.max(target / img.width, target / img.height);
        const w = img.width * ratio;
        const h = img.height * ratio;
        ctx.drawImage(img, (target - w) / 2, (target - h) / 2, w, h);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error("Could not read that image"));
      };
      img.src = url;
    });

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Pick an image file");
      return;
    }
    setBusy(true);
    try {
      const dataUrl = await resizeToDataUrl(file);
      const updated = await authApi.updateMe({ avatar_url: dataUrl });
      onChanged?.(updated);
      toast.success("Profile picture updated");
    } catch (err) {
      console.error(err);
      toast.error(formatError(err));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeAvatar = async (e) => {
    e?.stopPropagation();
    if (!hasPic) return;
    setBusy(true);
    try {
      const updated = await authApi.updateMe({ avatar_url: "" });
      onChanged?.(updated);
      toast.success("Profile picture removed");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  const dimension = `${size}px`;
  const fontSize = `${Math.max(12, Math.round(size * 0.42))}px`;

  // When editable we render the inner button (file picker); otherwise we
  // render a plain div so click events bubble up to a parent <Link> or
  // wrapping element. This keeps the whole avatar tappable when used as a
  // navigation button (e.g., the Dashboard header → Profile shortcut).
  const InnerVisual = (
    <>
      {hasPic ? (
        <img
          src={user.avatar_url}
          alt={user?.name ? `${user.name}'s avatar` : "User avatar"}
          className="w-full h-full object-cover"
          draggable={false}
        />
      ) : (
        <div
          className="w-full h-full bg-[#DCEDF0] text-[#1F5566] flex items-center justify-center font-heading font-bold"
          style={{ fontSize }}
          data-testid={`${testId}-initials`}
        >
          {initials}
        </div>
      )}
      {busy && (
        <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
          <Loader2 className="w-4 h-4 text-white animate-spin" />
        </div>
      )}
    </>
  );

  return (
    <div
      className="relative inline-block align-middle"
      style={{ width: dimension, height: dimension }}
      data-testid={testId}
    >
      {editable ? (
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={busy}
          aria-label="Change profile picture"
          className="relative rounded-full overflow-hidden block w-full h-full cursor-pointer ring-2 ring-transparent hover:ring-[#277A82]/50 transition-shadow"
        >
          {InnerVisual}
        </button>
      ) : (
        <div
          aria-label="Profile picture"
          className="relative rounded-full overflow-hidden block w-full h-full"
        >
          {InnerVisual}
        </div>
      )}

      {editable && (
        <>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            onChange={onFile}
            className="hidden"
            data-testid={`${testId}-file-input`}
          />
          {/* Tiny camera badge in the bottom-right when editable. */}
          <span
            className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-[#1F5566] text-white flex items-center justify-center shadow ring-2 ring-[#FDFBF7] pointer-events-none"
            aria-hidden="true"
          >
            <Camera className="w-3 h-3" />
          </span>
          {hasPic && !busy && (
            <button
              type="button"
              onClick={removeAvatar}
              data-testid={`${testId}-remove-btn`}
              aria-label="Remove profile picture"
              className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-[#E07A5F] hover:bg-[#C9603E] text-white flex items-center justify-center shadow ring-2 ring-[#FDFBF7]"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          )}
        </>
      )}
    </div>
  );
};

export default Avatar;
